import type { Geometry } from 'geojson';

import {
  DEFAULT_ICS_FORM_FAMILY,
  type IcsFormFamily,
} from 'components/IAP/formFamily';
import { defaultDraftSeats } from './draftRosterModel';
import {
  allGroupsMarked,
  createEmptyHwcgGroupActions,
  HWCG_GROUP_IDS,
  HWCG_GROUP_LABELS,
  type HwcgGroupActionsState,
  type HwcgGroupId,
} from './hwcgGroupActions';
import {
  createEmptyHwcgAuthorization,
  type HwcgAuthorizationState,
} from './hwcgAuthorization';
import {
  createEmptyHwcgEocNotice,
  type HwcgEocNoticeState,
} from './hwcgEocNotice';
import {
  createDefaultHwcgIncidentSpecifics,
  type HwcgIncidentSpecificsState,
} from './hwcgIncidentSpecifics';

export const RESPONSE_WORKFLOWS = {
  ICS: 'ics',
} as const;

export type ResponseWorkflow =
  (typeof RESPONSE_WORKFLOWS)[keyof typeof RESPONSE_WORKFLOWS];

/** Channels the backend understands for activation requests. */
export const ACTIVATION_CHANNELS = ['email', 'sms', 'voice', 'in_app'] as const;

export type ActivationChannel = (typeof ACTIVATION_CHANNELS)[number];

export const ACTIVATION_CHANNEL_LABELS: Record<ActivationChannel, string> = {
  email: 'Email',
  sms: 'Text message',
  voice: 'Automated call',
  in_app: 'In-app',
};

/**
 * Email, SMS and voice reproduce what activations sent unconditionally before
 * the channel picker existed; in-app is new so it stays off by default.
 */
export const DEFAULT_ACTIVATION_CHANNELS: ActivationChannel[] = [
  'email',
  'sms',
  'voice',
];

/** A person picked by hand, optionally slotted into an ICS seat. */
export type PersonPick = {
  user_guid: string;
  name: string;
  email_address: string;
  /** ICS position name; resolved to a seat id after the incident exists. */
  position: string | null;
};

export type DraftRosterSeat = {
  id: string;
  name: string;
  parentId: string | null;
  isWorkgroup?: boolean;
  /**
   * The seat's ICS element, carried so a loaded org template seeds the chart it
   * was saved from.
   */
  category?: string;
  customCategory?: string | null;
};

export type DraftRosterAssignment = {
  positionId: string;
  user_guid: string;
  name: string;
  email_address: string;
  isLeader?: boolean;
};

/** Shape createIncident accepts to seed the edited draft tree. */
export type RosterNodePayload = {
  name: string;
  parent: string | null;
  isWorkgroup?: boolean;
  category?: string;
  customCategory?: string | null;
};

export const DEFAULT_ROSTER_PARENT = 'Incident Commander';

export type CreateIncidentFormValues = {
  incident_name: string;
  incident_types: string[];
  related_events: string[];
  areas_of_responsibility: string[];
  response_workflow: ResponseWorkflow;
  ics_form_family: IcsFormFamily;
  sponsor_organization: string;
  location: Geometry | null;
  address: string | null;
  activated_teams: string[];
  people: PersonPick[];
  activation_channels: ActivationChannel[];
  activation_message: string;
  /** Catalog slug last loaded in the draft chart; null means default full ICS. */
  roster_template_slug: string | null;
  roster_template_name: string | null;
  roster_seats: DraftRosterSeat[];
  roster_assignments: DraftRosterAssignment[];
  /** HWCG filled roster applied from the selected plan. Null until a plan seeds it. */
  hwcg_filled_roster_template_id: string | null;
  /**
   * Contact-list people from that template who have no user account. They are
   * named on a seat and emailed or texted when the incident is created.
   */
  hwcg_roster_externals: {
    seatName: string;
    name: string;
    email: string;
    phone: string;
  }[];
  /** HWCG only; ignored and never persisted when the flag is off. */
  hwcg_specifics: HwcgIncidentSpecificsState;
  hwcg_group_actions: HwcgGroupActionsState;
  hwcg_eoc: HwcgEocNoticeState;
  hwcg_notices: {
    responsible_party: HwcgEocNoticeState;
    mutual_aid: HwcgEocNoticeState;
    service_provider: HwcgEocNoticeState;
  };
  hwcg_authorization: HwcgAuthorizationState;
  /** Plan library plan chosen on the HWCG Apply Plan step. */
  plan_id: string | null;
  plan_name: string | null;
};

export type CreateIncidentPrefill = {
  initialRelatedEvents?: string[];
  initialIncidentTypes?: string[];
  initialAorSelections?: { value: string; label: string }[];
};

export function buildDefaultValues({
  sponsorOrganization,
  prefill = {},
}: {
  sponsorOrganization: string;
  prefill?: CreateIncidentPrefill;
}): CreateIncidentFormValues {
  return {
    incident_name: '',
    incident_types: prefill.initialIncidentTypes ?? [],
    related_events: prefill.initialRelatedEvents ?? [],
    areas_of_responsibility: (prefill.initialAorSelections ?? []).map(
      (option) => option.value
    ),
    response_workflow: RESPONSE_WORKFLOWS.ICS,
    ics_form_family: DEFAULT_ICS_FORM_FAMILY,
    sponsor_organization: sponsorOrganization,
    location: null,
    address: null,
    activated_teams: [],
    people: [],
    activation_channels: [...DEFAULT_ACTIVATION_CHANNELS],
    activation_message: '',
    roster_template_slug: null,
    roster_template_name: null,
    roster_seats: defaultDraftSeats(),
    roster_assignments: [],
    hwcg_filled_roster_template_id: null,
    hwcg_roster_externals: [],
    hwcg_specifics: createDefaultHwcgIncidentSpecifics(),
    hwcg_group_actions: createEmptyHwcgGroupActions(),
    hwcg_eoc: createEmptyHwcgEocNotice(),
    hwcg_notices: {
      responsible_party: createEmptyHwcgEocNotice(),
      mutual_aid: createEmptyHwcgEocNotice(),
      service_provider: createEmptyHwcgEocNotice(),
    },
    hwcg_authorization: createEmptyHwcgAuthorization(),
    plan_id: null,
    plan_name: null,
  };
}

export const CREATE_INCIDENT_STEPS = [
  { id: 1, label: 'Name & Location' },
  { id: 2, label: 'Build Team' },
  { id: 3, label: 'Notifications' },
] as const;

/**
 * HWCG is intake then a required plan (roster comes from the plan's filled
 * template). Notifications drop out because who gets told is decided by the
 * Group Actions on step 1, not a channel picker.
 */
export const HWCG_CREATE_INCIDENT_STEPS = [
  { id: 1, label: 'Incident Specifics' },
  { id: 2, label: 'Apply Plan' },
] as const;

export type CreateIncidentStepId = (typeof CREATE_INCIDENT_STEPS)[number]['id'];

export function stepsFor(
  hwcg: boolean
): readonly { id: CreateIncidentStepId; label: string }[] {
  return hwcg ? HWCG_CREATE_INCIDENT_STEPS : CREATE_INCIDENT_STEPS;
}

export function lastStepFor(hwcg: boolean): CreateIncidentStepId {
  const steps = stepsFor(hwcg);
  return steps[steps.length - 1].id;
}

const HWCG_SPECIFIC_FIELDS: {
  key: keyof HwcgIncidentSpecificsState;
  label: string;
}[] = [
  { key: 'incidentName', label: 'Incident Name' },
  { key: 'companyName', label: 'Responsible Party' },
  { key: 'callerName', label: 'Caller Name' },
  { key: 'callbackNumber', label: 'Callback #' },
  { key: 'callerEmail', label: 'Caller Email' },
  { key: 'date', label: 'Date' },
  { key: 'time', label: 'Time' },
  { key: 'callTaker', label: 'HWCG Call Taker' },
  { key: 'latitude', label: 'Latitude' },
  { key: 'longitude', label: 'Longitude' },
  { key: 'blockNumber', label: 'Block + #' },
  { key: 'waterDepthFt', label: 'Water Depth (ft)' },
  { key: 'wellName', label: 'Well Name and #' },
  { key: 'drillRigName', label: 'Drill Rig Name' },
  { key: 'description', label: 'Incident Description' },
];

function hwcgNoticeFor(
  values: CreateIncidentFormValues,
  groupId: HwcgGroupId
): HwcgEocNoticeState {
  if (groupId === 'eoc') return values.hwcg_eoc ?? createEmptyHwcgEocNotice();
  return values.hwcg_notices?.[groupId] ?? createEmptyHwcgEocNotice();
}

export function hwcgNoticeErrors(
  groupId: HwcgGroupId,
  action: HwcgGroupActionsState[HwcgGroupId],
  notice: HwcgEocNoticeState
): string[] {
  if (action !== 'activate' && action !== 'notify') return [];
  const label = HWCG_GROUP_LABELS[groupId];
  const errors: string[] = [];
  if (notice.recipientContactIds.length === 0) {
    errors.push(`${label} recipients are required.`);
  }
  if (notice.channels.length === 0) {
    errors.push(`${label} communication method is required.`);
  }
  if (action === 'activate' && !notice.locationLabel.trim()) {
    errors.push(
      groupId === 'responsible_party'
        ? 'Report-to location is required.'
        : 'EOC location is required.'
    );
  }
  if (
    action === 'activate' &&
    groupId === 'responsible_party' &&
    !notice.reportToTime.trim()
  ) {
    errors.push('Report-to time is required.');
  }
  return errors;
}

function hwcgAuthorizationErrors(
  authorization: HwcgAuthorizationState | undefined
): string[] {
  const value = authorization ?? createEmptyHwcgAuthorization();
  const errors: string[] = [];
  if (!value.date.trim()) errors.push('Authorization date is required.');
  if (!value.time.trim()) errors.push('Authorization time is required.');
  if (!value.contactId.trim())
    errors.push('Authorized individual is required.');
  if (!value.caller.trim()) errors.push('HWCG caller is required.');
  if (!value.description.trim())
    errors.push('Authorization description is required.');
  if (!value.confirmed) errors.push('Authorization confirmation is required.');
  return errors;
}

/**
 * Only step 1 gates progress outside HWCG: team and notifications are optional,
 * matching the dialog this replaces. HWCG also requires the intake fields, a
 * group action, and a plan (on step 2).
 */
export function stepErrors(
  step: CreateIncidentStepId,
  values: CreateIncidentFormValues,
  hwcg = false
): string[] {
  if (hwcg && step === 2) {
    return values.plan_id ? [] : ['A plan is required.'];
  }
  if (step !== 1) return [];

  const errors: string[] = [];
  if (!hwcg && !values.incident_name.trim()) errors.push('Name is required.');
  if (hwcg) {
    const specifics = values.hwcg_specifics;
    HWCG_SPECIFIC_FIELDS.forEach((field) => {
      if (!specifics?.[field.key]?.trim()) {
        errors.push(`${field.label} is required.`);
      }
    });
    errors.push(...hwcgAuthorizationErrors(values.hwcg_authorization));
    if (!allGroupsMarked(values.hwcg_group_actions)) {
      errors.push('Choose an action for every group.');
    }
    HWCG_GROUP_IDS.forEach((groupId) => {
      errors.push(
        ...hwcgNoticeErrors(
          groupId,
          values.hwcg_group_actions[groupId],
          hwcgNoticeFor(values, groupId)
        )
      );
    });
  } else if (values.incident_types.length === 0) {
    errors.push('At least one incident category is required.');
  }
  if (!values.sponsor_organization)
    errors.push('Sponsor organization is required.');
  return errors;
}

export function isStepValid(
  step: CreateIncidentStepId,
  values: CreateIncidentFormValues,
  hwcg = false
): boolean {
  return stepErrors(step, values, hwcg).length === 0;
}

export function canCreate(
  values: CreateIncidentFormValues,
  hwcg = false
): boolean {
  if (!isStepValid(1, values, hwcg)) return false;
  if (hwcg && !isStepValid(2, values, hwcg)) return false;
  return true;
}

/** Plans tagged for the RP, plus untagged plans (usable until tagged). */
export function plansForResponsibleParty<
  T extends { responsiblePartyCompany?: string | null },
>(plans: T[], companyName: string | null | undefined): T[] {
  const company = companyName?.trim().toLowerCase() ?? '';
  return plans.filter((plan) => {
    const tagged = plan.responsiblePartyCompany?.trim();
    if (!tagged) return true;
    return tagged.toLowerCase() === company;
  });
}

export function stepSummary(
  step: CreateIncidentStepId,
  values: CreateIncidentFormValues,
  activatedUserCount: number,
  hwcg = false
): string {
  if (step === 1) {
    if (!values.incident_name.trim()) return 'Not named yet';
    return values.address?.trim()
      ? `${values.incident_name} — ${values.address}`
      : values.incident_name;
  }

  if (hwcg && step === 2) {
    return values.plan_name?.trim() || 'No plan';
  }

  if (step === 2) {
    const template = values.roster_template_name?.trim() || 'Full ICS';
    const seatCount = values.roster_seats?.length ?? 0;
    const people =
      activatedUserCount <= 1 ? 'You only' : `${activatedUserCount} people`;
    if (seatCount === 0) return people;
    const seats = seatCount === 1 ? '1 seat' : `${seatCount} seats`;
    return `${template} · ${seats} · ${people}`;
  }

  if (hwcg) return values.plan_name?.trim() || 'No plan';

  if (values.activation_channels.length === 0) return 'No notifications';
  return values.activation_channels
    .map((channel) => ACTIVATION_CHANNEL_LABELS[channel])
    .join(', ');
}

/** Shape the create endpoint expects for each person it should activate. */
export type ActivatedUserPayload = {
  user_guid: string;
  role: string;
  email_address: string;
  name: string;
};

type TeamActivatedUser = {
  user_guid: string;
  name: string;
  email_address: string;
  role: string;
};

const isPlaceholder = (value: string) => value === 'Loading...';

/**
 * Merges team-derived activations with hand-picked people. Team entries win on
 * conflict because their role carries the ICS team name, and users still being
 * fetched are dropped so the backend never stores "Loading...".
 */
export function mergeActivatedUsers(
  teamActivatedUsers: TeamActivatedUser[],
  people: PersonPick[]
): ActivatedUserPayload[] {
  const merged = new Map<string, ActivatedUserPayload>();

  for (const person of people) {
    if (!person.user_guid) continue;
    merged.set(person.user_guid, {
      user_guid: person.user_guid,
      role: person.position || 'Team Member',
      email_address: person.email_address,
      name: person.name,
    });
  }

  for (const user of teamActivatedUsers) {
    if (!user.user_guid) continue;
    if (isPlaceholder(user.name) || isPlaceholder(user.email_address)) continue;
    merged.set(user.user_guid, {
      user_guid: user.user_guid,
      role: user.role,
      email_address: user.email_address,
      name: user.name,
    });
  }

  return Array.from(merged.values());
}

export type PositionAssignment = {
  positionId: string;
  positionName: string;
  incidentMemberUserId: string;
  isLeader: boolean;
};

/**
 * ICS seats only exist once the incident row does, so the wizard collects
 * position names and resolves them against the seeded org chart afterwards.
 * Names with no matching seat are skipped; the person is still an incident
 * member through `activated_users`.
 */
export function findPositionIdByName(
  positions: { id: string; name: string }[],
  name: string
): string | undefined {
  return positions.find((position) => position.name === name)?.id;
}

export function resolvePositionAssignments(
  people: PersonPick[],
  positions: { id: string; name: string }[]
): PositionAssignment[] {
  const byName = new Map(
    positions.map((position) => [position.name, position.id])
  );

  return people.flatMap((person) => {
    if (!person.position || !person.user_guid) return [];
    const positionId = byName.get(person.position);
    if (!positionId) return [];
    return [
      {
        positionId,
        positionName: person.position,
        incidentMemberUserId: person.user_guid,
        isLeader:
          person.position === 'Incident Commander' ||
          person.position === 'TEMA Director',
      },
    ];
  });
}
